/**
 * Reading Carnaval's monthly stock report.
 *
 * Their sheet has a fixed shape: a Spanish date line in row 3, headers in row
 * 4, and data from row 5 — RM (the SKU), Descripción, U.M., STOCK.
 *
 * Everything here is defensive about that shape rather than trusting it. The
 * file arrives from outside the Hub, once a month, and a supplier nudging a
 * row or renaming a header should produce a clear refusal at the preview step
 * rather than a snapshot full of zeroes that nobody questions until they order
 * against it.
 */

export type MaterialCategory = 'spool' | 'bead' | 'charm';

export interface ParsedItem {
  sku: string;
  description: string;
  unitOfMeasure: string;
  stockQty: number;
  category: MaterialCategory;
}

export interface ParsedReport {
  reportDate: string;
  items: ParsedItem[];
  /**
   * True when no date line was found and today was used instead. The preview
   * says so out loud — a snapshot silently stamped with the wrong month would
   * be filed under the wrong month forever, and snapshots are immutable.
   */
  dateWasGuessed: boolean;
}

/**
 * A worksheet cell as text.
 *
 * SheetJS hands back strings, numbers, booleans, Dates and error objects. A
 * bare String() on a Date or an error cell yields something useless like
 * [object Object], which would silently become a SKU or a description, so
 * anything that is not a primitive is treated as empty instead.
 */
function cellText(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return '';
}

const MONTHS: Record<string, string> = {
  enero: '01',
  febrero: '02',
  marzo: '03',
  abril: '04',
  mayo: '05',
  junio: '06',
  julio: '07',
  agosto: '08',
  septiembre: '09',
  setiembre: '09',
  octubre: '10',
  noviembre: '11',
  diciembre: '12',
};

/**
 * "Al 31 de julio de 2026" → 2026-07-31.
 *
 * The report's own date, not the upload date: a report sent on the 3rd covers
 * the month that ended on the 31st, and dating it by arrival would quietly
 * misfile it.
 */
export function parseSpanishDate(line: string): string | null {
  const match = /(\d{1,2})\s+de\s+([a-zA-Zéó]+)\s+de\s+(\d{4})/i.exec(line);
  if (match === null) {
    return null;
  }
  const day = match[1] ?? '';
  const monthWord = match[2] ?? '';
  const year = match[3] ?? '';
  const month = MONTHS[monthWord.toLowerCase()];
  if (month === undefined) {
    return null;
  }
  return `${year}-${month}-${day.padStart(2, '0')}`;
}

/**
 * Category from the SKU, falling back to the unit and description.
 *
 * The SKU prefixes are the reliable signal — Carnaval's own coding scheme —
 * so they are checked first and the text heuristics only cover anything that
 * does not follow it. Charms are checked before beads because a charm's
 * description can mention beads.
 */
export function categoryOf(
  sku: string,
  unitOfMeasure: string,
  description: string,
): MaterialCategory {
  if (/^China\.King\.PET\./i.test(sku) || /cono/i.test(unitOfMeasure)) {
    return 'spool';
  }
  if (/^4o/i.test(sku) || /charm|dije|colgante/i.test(description)) {
    return 'charm';
  }
  if (/^China\.GlassBead\./i.test(sku) || /bead/i.test(description)) {
    return 'bead';
  }
  return 'bead';
}

/**
 * Reads a quantity from the sheet.
 *
 * Carnaval's report uses US convention — a dot is a decimal point and a comma
 * groups thousands — confirmed against the July file. That matters more than
 * it looks: spools are reported in FRACTIONS, and 15.87 Conos means just under
 * sixteen partial spools. An earlier draft of this treated a dot before three
 * digits as a thousands separator, which would have read 15.870 as fifteen
 * thousand and reported a nearly-empty spool as abundantly stocked.
 *
 * A cell that is already a number skips all of this: SheetJS gives real
 * numbers for numeric cells, and that path is exact.
 */
export function parseQuantity(raw: unknown): number {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : 0;
  }
  const text = cellText(raw).trim();
  if (text === '') {
    return 0;
  }
  // Commas group thousands and carry no meaning; anything else non-numeric is
  // a currency mark or a stray unit and is dropped.
  const value = Number(text.replace(/,/g, '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(value) ? value : 0;
}

/** The date line, wherever it sits in the header block. */
const DATE_LINE = /\bAl\s+\d{1,2}\s+de\s+[a-zA-ZéóÉÓ]+\s+de\s+\d{4}/i;

/**
 * Finds the row carrying the report date.
 *
 * Scans rather than indexes. The file has been seen with the date on row 4 and
 * on row 5 — Carnaval adds and removes title lines above it — and every time
 * the offset was hardcoded it broke on the next report. Ten rows is more
 * header than any version of this file has ever had.
 */
export function findReportDate(rows: unknown[][]): string | null {
  for (const row of rows.slice(0, 10)) {
    const line = (row ?? []).map(cellText).join(' ');
    if (DATE_LINE.test(line)) {
      const parsed = parseSpanishDate(line);
      if (parsed !== null) {
        return parsed;
      }
    }
  }
  return null;
}

/**
 * Finds the header row by its column names rather than its position, and
 * returns the index of the first DATA row after it.
 *
 * Same reasoning as the date: the headers move when the title block changes,
 * and a fixed offset silently reads the header row as a material or skips a
 * real one.
 */
export function findDataStart(rows: unknown[][]): number {
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const cells = (rows[i] ?? []).map((cell) => cellText(cell).trim().toLowerCase());
    const hasSku = cells.some((c) => c === 'rm');
    const hasStock = cells.some((c) => c === 'stock' || c === 'u.m.' || c.startsWith('descripc'));
    if (hasSku && hasStock) {
      return i + 1;
    }
  }
  // No header row recognised: fall back to the layout the July file used, so
  // an unfamiliar file still produces something the preview can be judged on.
  return 5;
}

/**
 * Turns the sheet's raw rows into a report.
 *
 * `rows` is the worksheet as an array of arrays, which is what both SheetJS
 * and the ExcelJS reader produce for a header-less read.
 */
export function parseCarnavalReport(rows: unknown[][]): ParsedReport {
  const found = findReportDate(rows);
  // No date is no longer fatal. Blocking the upload over a missing header line
  // would strand a whole month of real stock counts over a formatting change;
  // the preview shows the date and flags it as a guess so it can be caught
  // before saving.
  const reportDate = found ?? new Date().toISOString().slice(0, 10);

  const items: ParsedItem[] = [];
  for (const row of rows.slice(findDataStart(rows))) {
    const sku = cellText(row?.[0]).trim();
    // A row with no SKU is not a material. These sheets carry totals and notes
    // below the data, and reading those as stock would invent inventory.
    if (sku === '') {
      continue;
    }
    const description = cellText(row?.[1]).trim();
    const unitOfMeasure = cellText(row?.[2]).trim();
    items.push({
      sku,
      description,
      unitOfMeasure,
      stockQty: parseQuantity(row?.[3]),
      category: categoryOf(sku, unitOfMeasure, description),
    });
  }

  if (items.length === 0) {
    throw new Error('inventory_no_rows');
  }
  return { reportDate, items, dateWasGuessed: found === null };
}

export type StockLevel = 'ok' | 'low' | 'depleted';

/**
 * Spools run out in single figures; beads and charms are counted in thousands,
 * so one threshold for both would be meaningless. Zero is depleted either way.
 */
export function stockLevel(item: { category: MaterialCategory; stockQty: number }): StockLevel {
  if (item.stockQty <= 0) {
    return 'depleted';
  }
  const lowAt = item.category === 'spool' ? 5 : 10000;
  return item.stockQty <= lowAt ? 'low' : 'ok';
}

export const STOCK_LABELS: Record<StockLevel, string> = {
  ok: 'In stock',
  low: 'Low stock',
  depleted: 'Depleted',
};

export const INVENTORY_ERRORS: Record<string, string> = {
  inventory_no_rows: 'No stock rows found. Data should start on row 5.',
  inventory_snapshot_is_locked: 'That snapshot is locked. Upload a new report instead.',
};

export function inventoryErrorMessage(raw: string): string {
  const key = Object.keys(INVENTORY_ERRORS).find((code) => raw.includes(code));
  return (
    (key === undefined ? undefined : INVENTORY_ERRORS[key]) ??
    'That file could not be read. Check it is the standard stock report.'
  );
}
